import { SetStateAction } from "react";

import { AxiosRequestConfig, AxiosResponse } from "axios";
import { TFunction } from "i18next";
import moment, { Moment } from "moment";
import qs from "query-string";
import { AutocompleteInputChangeReason } from "@mui/material";

import { Task } from "components/Actions/dialogs/Devices/CreateTask/types";
import { FileType } from "components/Actions/dialogs/Files/types";
import { alphabeticalSort } from "components/Filters/constants";
import { PERMISSIONS, UserWithCompanyAttributes } from "context/auth/types";
import {
    AccessGroup,
    AUTHENTICATION_TYPES,
    CARRIER,
    COMPANY_FILTER,
    COMPANY_LEVELS,
    COMPANY_TYPES,
    DownloadFileParameters,
    ENDPOINTS,
    ENVIRONMENTS,
    FILE_TYPES,
    Filter,
    FILTER_NAMES,
    FilterDropdownOptions,
    FILTERS_WITH_QUOTES,
    ID_TYPE,
    INVALID,
    LANGUAGES,
    NOT_ASSIGNED,
    NOT_FILTER_NAMES,
    ORDERS,
    QueryParamName,
    QueryParams,
    QueryParamValue,
    Resource,
    ResourceLimitSettings,
    ROOT_COMPANY,
    SPEC_ID_NOT_ASSIGNED_VALUE,
    TABLE_NAMES,
    TASK_STATUSES,
    TELTONIKA_FILES_COMPANY,
    TRANSLATABLE_FILTER_NAMES,
    USER_ACCESS_LEVELS,
    Vehicle
} from "shared";

export const noop = () => {
    // This is intentional
};

export const getValidLanguage = (lang: any, defaultLang: LANGUAGES) => {
    const isValid = Object.values(LANGUAGES).includes(lang);

    return isValid ? (lang as LANGUAGES) : defaultLang;
};

export const isValidParamName = (paramName: string) => {
    const allNames = Object.values({ ...FILTER_NAMES, ...NOT_FILTER_NAMES });
    return allNames.includes(paramName as any);
};

export const isRootCompanyOrCompanyIdFilter = (
    paramName: FILTER_NAMES | NOT_FILTER_NAMES
) =>
    paramName === FILTER_NAMES.RootCompany ||
    paramName === FILTER_NAMES.CompanyId;

export const isFilter = (paramName: string) =>
    Object.values(FILTER_NAMES).includes(paramName as any);

export const isFilterOrSearch = (paramName: string) =>
    isFilter(paramName) ||
    paramName === NOT_FILTER_NAMES.Query ||
    paramName === NOT_FILTER_NAMES.QueryField;

export const isTranslatableFilter = (filterName: string) =>
    Object.values(TRANSLATABLE_FILTER_NAMES).includes(filterName as any);

export const isAuthorizedToDownload = (
    fileType: string,
    userAccessLevel: number | undefined
) => {
    if (userAccessLevel === undefined) {
        return false;
    }

    switch (fileType) {
        // Requires at least User access level
        case FILE_TYPES.BlackBox:
        case FILE_TYPES.BluetoothAuthorizationList:
        case FILE_TYPES.CanDebugLog:
        case FILE_TYPES.CarrierAdvanceFw:
        case FILE_TYPES.CarrierAdvanceSettings:
        case FILE_TYPES.CarrierApxFw:
        case FILE_TYPES.CarrierApxSettings:
        case FILE_TYPES.CarrierExtractions:
        case FILE_TYPES.CarrierLoggerFiles:
        case FILE_TYPES.Configuration:
        case FILE_TYPES.DeviceExportFileCsv:
        case FILE_TYPES.DeviceExportFileXlsx:
        case FILE_TYPES.Generic:
        case FILE_TYPES.Log:
        case FILE_TYPES.ManualGeofenceList:
        case FILE_TYPES.OneWireAuthorizationList:
        case FILE_TYPES.WdLog:
            return userAccessLevel >= USER_ACCESS_LEVELS.User;

        // Requires at least Support access level
        case FILE_TYPES.CrashDump:
        case FILE_TYPES.DeviceImportFile:
        case FILE_TYPES.ExceptionInfo:
        case FILE_TYPES.NmeaLog:
            return userAccessLevel >= USER_ACCESS_LEVELS.Support;

        default:
            return false;
    }
};

export const isFailedOrCompletedWithErrorsTask = (statusId: TASK_STATUSES) =>
    statusId === TASK_STATUSES.Failed ||
    statusId === TASK_STATUSES.CompletedWithErrors;

export const isPendingOrRunningTask = (statusId: TASK_STATUSES) =>
    statusId === TASK_STATUSES.Pending || statusId === TASK_STATUSES.Running;

export const isRefreshableTaskType = (statusId: TASK_STATUSES) =>
    isPendingOrRunningTask(statusId) ||
    statusId === TASK_STATUSES.Verifying ||
    statusId === TASK_STATUSES.Failed ||
    statusId === TASK_STATUSES.CompletedWithErrors;

export const filteredExistingItems = (
    options: Filter[],
    filterValue: string | string[] | undefined | null
) =>
    options.filter(option => {
        const isArray = Array.isArray(filterValue);
        const id = String(option.id);

        return isArray ? filterValue.includes(id) : id === String(filterValue);
    });

export const getRootCompanyValue = (
    allOptions: FilterDropdownOptions,
    rootCompanyId: string
) => {
    const rootCompanies = allOptions[FILTER_NAMES.RootCompany] || [];

    return filteredExistingItems(rootCompanies, rootCompanyId)[0];
};

export const getFilteredQueryParams = (
    params: QueryParams,
    includeSearch?: boolean
) => {
    const allParams = { ...params };
    const allParamNames = Object.keys(allParams) as QueryParamName[];

    allParamNames.forEach(paramName => {
        const mustBeDeleted = includeSearch
            ? !isFilterOrSearch(paramName)
            : !isFilter(paramName);

        if (mustBeDeleted) {
            delete allParams[paramName];
        }
    });

    return allParams;
};

export const getStringifiedParams = (params: QueryParams) => {
    const copyParams = { ...params };
    delete copyParams[NOT_FILTER_NAMES.Selected];

    return qs.stringify(copyParams, {
        arrayFormat: "comma"
    });
};

export const getIds = (rows: any, idType: ID_TYPE) =>
    rows.map((row: any) => row[idType]);

export const handleFilterOptionsStructure = (
    options: Filter[],
    filterName: FILTER_NAMES,
    tableName: TABLE_NAMES,
    taskTypes: Task[],
    t: TFunction<"translation", undefined>
) => {
    const isTypeFilterName = filterName === FILTER_NAMES.Type;

    const nameForSorting = (
        isTypeFilterName ? `${tableName.slice(0, -1)}Type` : filterName
    ) as FILTER_NAMES | TRANSLATABLE_FILTER_NAMES;

    const sortedOptions = sortAndReturnFilterDropdown(
        options,
        nameForSorting,
        t,
        taskTypes
    );

    moveCarrierToBottom(sortedOptions);
    moveInvalidToBottom(sortedOptions);
    addNotAssigned(sortedOptions, filterName);

    return sortedOptions;
};

const sortAndReturnFilterDropdown = (
    options: Filter[],
    filterName: FILTER_NAMES | TRANSLATABLE_FILTER_NAMES,
    t: TFunction<"translation", undefined>,
    taskTypes?: Task[]
) => {
    const isTaskTypeFilter =
        filterName === FILTER_NAMES.TaskType ||
        filterName === TRANSLATABLE_FILTER_NAMES.TaskType;

    isTaskTypeFilter && taskTypes && sortTaskTypes(options, taskTypes);

    const shouldBeSortedAlphabetically = alphabeticalSort.includes(filterName);

    return shouldBeSortedAlphabetically
        ? sortAlphabetically(filterName, options, t)
        : options;
};

const sortTaskTypes = (options: Filter[], taskTypes: Task[]) => {
    const taskOrderMap = taskTypes.reduce<Record<string, number>>(
        (acc, task, index) => {
            acc[task.type] = index;
            return acc;
        },
        {}
    );

    const sortedTaskTypes = () => {
        return options.sort((a, b) => {
            const indexA = taskOrderMap[a.id] ?? Infinity; // Use Infinity for missing items
            const indexB = taskOrderMap[b.id] ?? Infinity;

            return indexA - indexB;
        });
    };

    return sortedTaskTypes();
};

export const sortAlphabetically = (
    filterName: string,
    options: Filter[],
    t: TFunction<"translation", undefined>
) => {
    if (isTranslatableFilter(filterName)) {
        const optionsWithTranslations = options.map(option => {
            const shouldTranslate =
                option.id !== null && option.id !== "Invalid";

            const getTranslatedName = () => {
                if (shouldTranslate) {
                    return t(`General##${option.name}`);
                }

                return option.name;
            };

            return {
                ...option,
                translatedName: getTranslatedName()
            };
        });

        const sortedArray = sortAndReturnArray(
            optionsWithTranslations,
            "translatedName"
        );

        return sortedArray.map(({ id, name }) => ({ id, name }));
    }

    return sortAndReturnArray(options, "name");
};

export const moveCarrierToBottom = (options: (Filter | FileType)[]) => {
    const carrierArray: (Filter | FileType)[] = [];

    for (let i = options.length - 1; i >= 0; i--) {
        const option = options[i];

        const id = "value" in option ? option.value : option.id;

        const isCarrierFileType =
            typeof id === "string" && id.includes(CARRIER);

        if (isCarrierFileType) {
            const actualValue = options.splice(i, 1);

            carrierArray.unshift(...actualValue);
        }
    }

    options.push(...carrierArray);
};

const moveInvalidToBottom = (options: Filter[]) => {
    options.forEach((field: Filter, i: number) => {
        if (field.id === INVALID) {
            const invalidValue = options.splice(i, 1);
            options.push(invalidValue[0]);
        }
    });
};

const addNotAssigned = (options: Filter[], filterName: FILTER_NAMES) => {
    const isSpecId = isSpecIdFilter(filterName);

    if (isSpecId) {
        const shouldHaveNotAssigned = options.some(
            ({ id }) => id === null || id === 0 || id === 1
        );

        for (let i = options.length - 1; i >= 0; i--) {
            const id = options[i].id;

            if (id === null || id === 0 || id === 1) {
                options.splice(i, 1);
            }
        }

        shouldHaveNotAssigned &&
            options.unshift({
                id: SPEC_ID_NOT_ASSIGNED_VALUE,
                name: NOT_ASSIGNED
            });
    } else {
        options.forEach((field: Filter, i: number) => {
            if (field.id === null) {
                options.splice(i, 1);

                options.unshift({
                    id: "null",
                    name: NOT_ASSIGNED
                });
            }
        });
    }
};

export const parseResource = (resource: Resource, data: any): string | null => {
    const isNestedResource = Array.isArray(resource);

    if (isNestedResource) {
        return resource.reduce((object, objectKey) => {
            return object !== null ? object[objectKey] : null;
        }, data);
    } else {
        return data[resource];
    }
};

export const generateColor = (
    inverted: boolean | undefined,
    firstColor: string,
    secondColor: string
) => (inverted ? firstColor : secondColor);

export const isGlobalFile = (companyId: number) =>
    companyId === TELTONIKA_FILES_COMPANY;

const getDownloadedFileName = (contentDisposition: string) => {
    const encodedMatch = RegExp(/filename\*=UTF-8''([^;\n]*)/i).exec(
        contentDisposition
    );

    if (encodedMatch) {
        try {
            return decodeURIComponent(encodedMatch[1]);
        } catch {
            return encodedMatch[1];
        }
    }

    const fallbackMatch = RegExp(/filename="?([^";\n]*)"?/i).exec(
        contentDisposition
    );

    if (fallbackMatch) {
        return fallbackMatch[1];
    }

    return "downloaded_file";
};

export const downloadFile = async (parameters: DownloadFileParameters) => {
    const {
        id,
        getData,
        handleResponse,
        customEndpoint,
        setLoading,
        removeLoading
    } = parameters;

    try {
        setLoading?.();

        const endpoint = customEndpoint ?? `${ENDPOINTS.Files}/download/${id}`;
        const response = await getData(endpoint, { responseType: "blob" });

        const contentDisposition =
            response.headers["content-disposition"] ?? "";

        const fileName = getDownloadedFileName(contentDisposition);

        const blob = response.data;
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    } catch (error) {
        handleResponse(error);
    }

    removeLoading?.();
};

export const isAnalyticsCookieAccepted = (cookieConsent: any) =>
    cookieConsent.analytics === "true";

export const getEnvironment = () => {
    const env = import.meta.env.VITE_ENVIRONMENT;

    return {
        env,
        isProductionEnv:
            env === ENVIRONMENTS.Production ||
            env === ENVIRONMENTS.TrProduction,
        isTestEnv: env === ENVIRONMENTS.Staging,
        isDevelopmentEnv:
            env === ENVIRONMENTS.Dev ||
            env === ENVIRONMENTS.TrDev ||
            env === ENVIRONMENTS.Development ||
            env === ENVIRONMENTS.DevelopmentLocalApi,
        isNotLocalEnv:
            env !== ENVIRONMENTS.Development &&
            env !== ENVIRONMENTS.DevelopmentLocalApi,
        isFotaGlobalEnv:
            env === ENVIRONMENTS.Production ||
            env === ENVIRONMENTS.Staging ||
            env === ENVIRONMENTS.Dev ||
            env === ENVIRONMENTS.Development ||
            env === ENVIRONMENTS.DevelopmentLocalApi
    };
};

export const getFilteredCompaniesIds = (companyFilteredValues: Filter[]) => {
    return companyFilteredValues.map(company => company.id);
};

export const getCompanyFilterQueryParamsFromBrowser = (
    filterName: FILTER_NAMES.RootCompany | FILTER_NAMES.CompanyId
) => {
    try {
        const localStorageName =
            filterName === FILTER_NAMES.RootCompany
                ? ROOT_COMPANY
                : COMPANY_FILTER;

        const value: string | string[] = JSON.parse(
            localStorage.getItem(localStorageName) ?? ""
        );

        return value.length > 0 ? { [filterName]: value } : {};
    } catch {
        return {};
    }
};

export const getQueryParamsFromBrowser = (resource: TABLE_NAMES) => {
    try {
        return (
            JSON.parse(sessionStorage.getItem(`${resource}Query`) ?? "") ?? {}
        );
    } catch {
        return {};
    }
};

export const getFirstLetterUppercase = (value: string) => {
    if (value.length) {
        return value[0].toUpperCase() + value.slice(1);
    }

    return value;
};

export const isValidNumberValue = (value: string) => {
    const regexNumber = /^[0-9\b]+$/;

    return value === "" || regexNumber.test(value);
};

export const getFilterParamsWithoutRoot = (filterParams: QueryParams) => {
    const copiedParams = { ...filterParams };

    delete copiedParams[FILTER_NAMES.RootCompany];

    return copiedParams;
};

export const generateArrayOfNumbers = (count: number) =>
    Array.from({ length: count }, (_, i) => i + 1);

export const isPremiumLevel = (level: COMPANY_LEVELS | undefined) =>
    level === COMPANY_LEVELS.Premium;

export const isClientTypeCompany = (type: any) => type === COMPANY_TYPES.Client;

export const isInternalTypeCompany = (type: any) =>
    type === COMPANY_TYPES.Internal;

export const isAllowedToEditCompany = (
    userAccessLevel: USER_ACCESS_LEVELS,
    companyType: COMPANY_TYPES | undefined,
    companyCode?: string
) => {
    if (isInternalTypeCompany(companyType)) {
        return userAccessLevel >= USER_ACCESS_LEVELS.SuperUser;
    }

    if (companyCode) {
        return userAccessLevel >= USER_ACCESS_LEVELS.InternalIntegration;
    }

    return true;
};

export const isInternalOrPremiumLevel = (
    companyLevel: COMPANY_LEVELS | undefined
) => companyLevel === COMPANY_LEVELS.Internal || isPremiumLevel(companyLevel);

export const isNumericValue = (value: string) => {
    const regexValue = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;

    return regexValue.test(value);
};

export const isNumericIntegerValue = (value: string) => {
    const regexValue = /^\d+$/;

    return regexValue.test(value);
};

export const disableDates = (
    momentDate: Moment,
    durationAmount: moment.DurationInputArg1,
    durationUnit: moment.unitOfTime.DurationConstructor,
    includeToday: boolean = true
) => {
    const today = moment();

    const referenceDate = includeToday
        ? today
        : today.clone().subtract(1, "day");

    const maxAllowedDate = referenceDate
        .clone()
        .add(durationAmount, durationUnit);

    const isAfterMaxDate = moment(momentDate).isAfter(maxAllowedDate, "day");

    if (includeToday) {
        return moment(momentDate).isSame(today, "day") || isAfterMaxDate;
    }

    return isAfterMaxDate;
};

export const isEnglish = (language: LANGUAGES) =>
    language === LANGUAGES.English;

export const isSpecIdFilter = (filterName: FILTER_NAMES) =>
    filterName === FILTER_NAMES.SpecId;

export const getFiltersWithQuotes = () => Object.values(FILTERS_WITH_QUOTES);

const specIdHasAllNotAssignedParts = (valueArray: string[]) =>
    valueArray.includes("null") &&
    valueArray.includes("0") &&
    valueArray.includes("1");

const hasNoSpecIdNotAssigned = (value: string) =>
    value !== "null" && value !== "0" && value !== "1";

export const transformIdArrayWithSpecIdNotAssigned = (
    filterName: FILTER_NAMES,
    idArray: string[]
) => {
    const isSpecId = isSpecIdFilter(filterName);

    if (isSpecId) {
        const hasNotAssigned = specIdHasAllNotAssignedParts(idArray);

        return hasNotAssigned
            ? [
                  ...idArray.filter(hasNoSpecIdNotAssigned),
                  SPEC_ID_NOT_ASSIGNED_VALUE
              ]
            : idArray;
    }

    return idArray;
};

export const updateParamsWithCorrectSpecIdValue = (params: QueryParams) => {
    const updatedQueryParams = { ...params };
    const specIdFilterValue = updatedQueryParams[FILTER_NAMES.SpecId];

    if (specIdFilterValue) {
        const isArray = Array.isArray(specIdFilterValue);
        const valueArray = isArray ? specIdFilterValue : [specIdFilterValue];

        // Normalize zero-like values (e.g., "00", "000") to "0"
        const normalizedValueArray = valueArray.map(value => {
            if (/^0+$/.test(value)) {
                return "0";
            }

            return value;
        });

        const hasNotAssignedPart =
            normalizedValueArray.includes("null") ||
            normalizedValueArray.includes("0") ||
            normalizedValueArray.includes("1");

        if (hasNotAssignedPart) {
            const hasAllNotAssignedAllParts =
                specIdHasAllNotAssignedParts(normalizedValueArray);

            if (!hasAllNotAssignedAllParts) {
                updatedQueryParams[FILTER_NAMES.SpecId] =
                    normalizedValueArray.filter(hasNoSpecIdNotAssigned);
            }
        }
    }

    return updatedQueryParams;
};

export const getVehicle = (data: {
    model: string;
    startYear: number | null;
    endYear: number | null;
    make?: string;
}) => {
    const { model, startYear, endYear, make } = data;

    const getMake = () => {
        if (!make) return "";

        return `${make.charAt(0).toUpperCase()}${make.slice(1)} `;
    };

    const getYear = () => {
        if (startYear === null && endYear === null) return "";

        const start = startYear ?? "";
        const end = endYear !== null ? `-${endYear}` : "+";
        return ` (${start}${end})`;
    };

    return `${getMake()}${model}${getYear()}`;
};

export const renderCanOemFileVehicle = (
    vehicle: Vehicle | null,
    vehicleId: number | null,
    t: TFunction<"translation", undefined>
) => {
    if (vehicleId === 1) {
        return "Autoscan";
    }

    if (vehicle) {
        const { make, model, start_year, end_year } = vehicle;

        return getVehicle({
            make,
            model,
            startYear: start_year,
            endYear: end_year
        });
    }

    if (vehicleId !== null) {
        return `${t("General##unknown")} (${t("General##id")}: ${vehicleId})`;
    }

    return null;
};

export const isAuthenticatorType = (type: AUTHENTICATION_TYPES | null) =>
    type === AUTHENTICATION_TYPES.Authenticator;

export const sortAndReturnArray = <T>(
    data: T[],
    property?: keyof T,
    order: ORDERS = ORDERS.Ascending
): T[] => {
    const applyOrder = (comparison: number) =>
        order === ORDERS.Ascending ? comparison : -comparison;

    return [...data].sort((a, b) => {
        const valueA = property ? a[property] : a;
        const valueB = property ? b[property] : b;

        if (!valueA && !valueB) return 0;
        if (!valueA) return 1;
        if (!valueB) return -1;

        if (typeof valueA === "string" && typeof valueB === "string") {
            const comparison = valueA.localeCompare(valueB);
            return applyOrder(comparison);
        }

        if (typeof valueA === "number" && typeof valueB === "number") {
            const comparison = valueA - valueB;
            return applyOrder(comparison);
        }

        return 0;
    });
};

export const sendCodeToEmail = async (
    setLoading: (value: SetStateAction<boolean>) => void,
    successCallback: () => void,
    postData: (
        resource: string,
        payload?: any
    ) => Promise<AxiosResponse<any, any>>,
    handleResponse: (res: any) => void
) => {
    try {
        setLoading(true);
        await postData(ENDPOINTS.TfaSendEmail);
        successCallback();
    } catch (error) {
        handleResponse(error);
    }

    setLoading(false);
};

export const isLithuanianLanguage = (language: LANGUAGES) =>
    language === LANGUAGES.Lithuanian;

export const getCustomResourceLimits = async (
    companyId: number | string,
    getData: (
        resource: string,
        config?: AxiosRequestConfig<any>
    ) => Promise<AxiosResponse<any, any>>,
    setLoading?: () => void
) => {
    try {
        setLoading?.();

        const { data } = await getData(
            `${ENDPOINTS.Companies}/${companyId}/customResourceLimits`
        );

        return data as ResourceLimitSettings;
    } catch {
        return null;
    }
};

export const safeDecode = (value: string): string => {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

export const isValidImei = (imei: string) => {
    if (!/^\d{15}$/.test(imei)) return false;

    const digits = imei.split("").map(Number);
    const checkDigit = digits.pop()!;
    const reversed = digits.toReversed();

    const sum = reversed
        .map((digit, index) =>
            index % 2 === 0
                ? ((digit * 2) % 10) + Math.floor((digit * 2) / 10)
                : digit
        )
        .reduce((a, b) => a + b, 0);

    const luhnCheck = (10 - (sum % 10)) % 10;

    return checkDigit === luhnCheck;
};

export const getPermissionActionLabel = (
    permission: PERMISSIONS,
    t: TFunction<"translation", undefined>
) => {
    const [, action] = permission.split(".");

    return t(`Permission actions##${action}`);
};

export const handleAutocompleteInputValueChange = (
    reason: AutocompleteInputChangeReason,
    setInputValue: () => void
) => {
    if (
        reason !== "reset" &&
        reason !== "selectOption" &&
        reason !== "removeOption"
    ) {
        setInputValue();
    }
};

export const doesAccessLevelAllowToManageUser = (
    user: UserWithCompanyAttributes,
    optionAccessLevel: number
) => {
    const accessLevel = user.access_level;
    const isSuperUser = accessLevel >= USER_ACCESS_LEVELS.SuperUser;

    if (isSuperUser) return true;

    const isSystemUser = accessLevel >= USER_ACCESS_LEVELS.SystemUser;
    const isInternalIntegrationUser =
        accessLevel >= USER_ACCESS_LEVELS.InternalIntegration;
    const isAdminManagerUser = accessLevel >= USER_ACCESS_LEVELS.AdminManager;

    if (isSystemUser || isInternalIntegrationUser || isAdminManagerUser) {
        return optionAccessLevel < USER_ACCESS_LEVELS.AdminManager;
    }

    return optionAccessLevel === USER_ACCESS_LEVELS.User;
};

export const isUserHimself = (loggedUserId: number, id: number) =>
    loggedUserId === id;

export const handleSearchQueryParams = (
    query: QueryParamValue,
    queryField: QueryParamValue
) => {
    const emptyParams = {
        [NOT_FILTER_NAMES.Query]: "",
        [NOT_FILTER_NAMES.QueryField]: null
    };

    if ((query && !queryField) || (!query && queryField)) return emptyParams;

    if (query && queryField) {
        return query.length > 0
            ? {
                  [NOT_FILTER_NAMES.Query]: query,
                  [NOT_FILTER_NAMES.QueryField]: queryField
              }
            : emptyParams;
    }

    return {};
};

export const isNotEmpty = (value: any) => value !== null && value !== undefined;

export const sortAccessGroupOptions = (options: AccessGroup[]) =>
    sortAndReturnArray(options, "name");
